import React, { useState, useEffect, useRef } from 'react';
import {
    Box, TextField, Button, Typography, InputAdornment, IconButton,
    OutlinedInput, InputLabel, FormControl, Alert, Backdrop, CircularProgress
} from '@mui/material';
import { Email, Visibility, VisibilityOff } from '@mui/icons-material';
import { BlueRectangle, LoginContainer, LoginForm } from './utils/LoginUtils.js';
import { login, send2FACode, sendResetPasswordEmail, ssoFlowbirdCallback, validate2FACode } from '../_endpoints_/_api/api.js';
import { User } from '../datamodels/User.js';
import { getLoginUrl, getSsoRedirectUri } from '../_services/_hostingenvironments_/hostingEnvironments.js';
import { buildKeycloakAuthUrl, clearSsoSession, randomString, readSsoSession, saveSsoSession } from './utils/sso.ts';
import { getTranslationsForLanguage } from './utils/InitializeTranslations.js';
import Cookies from 'js-cookie';
import ForgotPassword from './ForgotPassword.tsx';

const Login: React.FC = () => {
    const userLanguage = navigator.language.split('-')[0];
    const translations = getTranslationsForLanguage(userLanguage);

    const [user, setUser] = useState<User>({ mail: '', password: '' });
    const [showPassword, setShowPassword] = useState<boolean>(false);
    const [token, setToken] = useState<string>('');
    const [errorMessage, setErrorMessage] = useState<string>('');
    const [loading, setLoading] = useState<boolean>(false);

    const [requires2FA, setRequires2FA] = useState<boolean>(false);
    const [twoFACodeInput, setTwoFACodeInput] = useState<string>('');
    const [userDataBackup, setUserDataBackup] = useState<any>(null);
    const [userId, setUserId] = useState<string>('');

    const [forgotPasswordUI, setForgotPasswordUI] = useState<boolean>(false);
    const [successMessage, setSuccessMessage] = useState<any>(null);

    // true from the very first render when the URL says SSO, so the form never flashes
    const [ssoInProgress, setSsoInProgress] = useState<boolean>(() => {
        const params = new URLSearchParams(window.location.search);
        return params.get('sso') === 'flowbird' || params.has('code');
    });
    // React 18 StrictMode invokes mount effects twice; an authorization code is single-use
    const ssoHandled = useRef<boolean>(false);

    const stripQuery = () => {
        window.history.replaceState({}, document.title, window.location.pathname);
    };

    useEffect(() => {
        if (ssoHandled.current) return;
        ssoHandled.current = true;

        const params = new URLSearchParams(window.location.search);

        if (params.get('error')) {
            clearSsoSession();
            stripQuery();
            setErrorMessage(translations.sso_error);
            setSsoInProgress(false);
            return;
        }

        if (params.get('sso') === 'flowbird') {
            try {
                const state = randomString();
                const nonce = randomString();
                const redirectUri = getSsoRedirectUri();
                saveSsoSession(state, nonce, redirectUri);
                // replace() so the Back button does not land on a page that re-triggers SSO
                window.location.replace(buildKeycloakAuthUrl(state, nonce, redirectUri));
            } catch {
                setErrorMessage(translations.sso_error);
                setSsoInProgress(false);
            }
            return;
        }

        const code = params.get('code');
        if (!code) return;

        const session = readSsoSession();
        clearSsoSession();  // single use
        stripQuery();       // before the network call: a mid-flight reload must not replay the code

        if (!session.state || session.state !== params.get('state')) {
            setErrorMessage(translations.sso_error);
            setSsoInProgress(false);
            return;
        }

        const exchangeCode = async () => {
            try {
                const data: any = await ssoFlowbirdCallback(code, session.redirectUri);
                setToken(data.access_token);  // the token useEffect below does the hand-off
            } catch {
                setErrorMessage(translations.sso_error);
                setSsoInProgress(false);
            }
        };
        exchangeCode();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleClickShowPassword = () => setShowPassword((show) => !show);

    const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
    };

    const handleChange = (prop: keyof User) => (event: React.ChangeEvent<HTMLInputElement>) => {
        setUser({ ...user, [prop]: event.target.value });
    };

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setLoading(true);
        setErrorMessage('');

        try {
            const userData: any = await login(user.mail, user.password);
            setUserId(userData.userid);

            if (userData.enabled_2fa) {
                const existingCookie = Cookies.get('2fa_token');
                if (!existingCookie) {
                    await send2FACode(userData.userid);
                    setRequires2FA(true);
                    setUserDataBackup(userData);
                    return;
                }
            }

            setToken(userData.token);
            setShowPassword(false);
        } catch (error) {
            setErrorMessage(translations.login_error);
        } finally {
            setLoading(false);
        }
    };

    const handleValidate2FACode = async () => {
        setLoading(true);
        setErrorMessage('');

        try {
            const isValid = await validate2FACode(userId, twoFACodeInput);
            if (isValid) {
                Cookies.set('2fa_token', userDataBackup.token, { expires: 365 }); // 1 any
                setToken(userDataBackup.token);
            } else {
                setErrorMessage(translations.incorrect_code);
            }
        } catch {
            setErrorMessage(translations.error_code);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (token !== '') {
            const url = getLoginUrl() + 'configuration?access_token=' + token;
            window.location.href = url;
        }
    }, [token]);

    const handleSendForgotPasswordEmail = async (email: string) => {
        setLoading(true);
        setErrorMessage('');
        try {
            const response = await sendResetPasswordEmail(email);
            if (response.error) {
                setErrorMessage(translations.error_sending_email);
            } else {
                setSuccessMessage(translations.forgot_password_email_sent);
                setUser({ ...user, mail: email });
            }
        } catch (error) {       
            setErrorMessage(translations.error_sending_email);
        } finally {
            setLoading(false);
        }
    };

    const handleBackToLogin = () => {
        setForgotPasswordUI(false);
        setSuccessMessage(null);
        setErrorMessage('');
    };

    return (
        <LoginContainer>
            <Backdrop open={loading || ssoInProgress} style={{ zIndex: 10000, color: '#fff' }}>
                <Box display="flex" flexDirection="column" alignItems="center">
                    <CircularProgress color="inherit" />
                    {ssoInProgress && (
                        <Typography variant="body2" mt={2}>{translations.sso_signing_in}</Typography>
                    )}
                </Box>
            </Backdrop>
        
            <Box width="fit-content" style={{minWidth: "450px"}} mt={2} mb={2} borderRadius={0}>
            <BlueRectangle style={{ width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <img src={require('../assets/u-admin.png')} alt="U-Admin" style={{ height: '50px' }} />
            </BlueRectangle>
                {ssoInProgress ? null : forgotPasswordUI ? (
                    <ForgotPassword
                        handleSendForgotPasswordEmail={handleSendForgotPasswordEmail}
                        handleBackToLogin={handleBackToLogin}
                        errorMessage={errorMessage}
                        successMessage={successMessage}
                        translations={translations}
                        userEmail={user.mail}
                    />
                ) : (   
                    <LoginForm elevation={3}>
                        <Typography variant="body2" align="center" gutterBottom>
                        { !requires2FA ? translations.sign_in : translations.introduce_code }
                        </Typography>

                        {!requires2FA ? (
                            <form onSubmit={handleSubmit}>
                                <Box mb={2} mt={2}>
                                    <TextField
                                        fullWidth
                                        label={translations.email}
                                        variant="outlined"
                                        required
                                        value={user.mail}
                                        onChange={handleChange('mail')}
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <Email />
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                </Box>
                                <Box mb={2}>
                                    <FormControl sx={{ width: '100%' }} variant="outlined">
                                        <InputLabel htmlFor="outlined-adornment-password">{translations.password}</InputLabel>
                                        <OutlinedInput
                                            id="outlined-adornment-password"
                                            type={showPassword ? 'text' : 'password'}
                                            required
                                            value={user.password}
                                            onChange={handleChange('password')}
                                            endAdornment={
                                                <InputAdornment position="end">
                                                    <IconButton
                                                        aria-label="toggle password visibility"
                                                        onClick={handleClickShowPassword}
                                                        onMouseDown={handleMouseDownPassword}
                                                        edge="end"
                                                    >
                                                        {showPassword ? <VisibilityOff /> : <Visibility />}
                                                    </IconButton>
                                                </InputAdornment>
                                            }
                                            label={translations.password}
                                        />
                                    </FormControl>
                                </Box>
                                {/* Forgot password link */}
                                <Box mb={2} textAlign="left">
                                    <Typography variant="body2">
                                        <span 
                                        onClick={() => setForgotPasswordUI(true)}
                                        style={{ color: '#007bff', 
                                        textDecoration: 'underline', 
                                        cursor: 'pointer' }}>{translations.forgot_password}</span>
                                    </Typography>
                                </Box>
                                {errorMessage && (
                                    <Box mb={2}>
                                        <Alert severity="error">{errorMessage}</Alert>
                                    </Box>
                                )}
                                <Box textAlign="center">
                                    <Button
                                        variant="contained"
                                        style={{ backgroundColor: 'rgba(0, 192, 239, 1)', color: 'white', padding: '8px 64px' }}
                                        type="submit"
                                    >
                                        {translations.enter}
                                    </Button>
                                </Box>
                            </form>
                        ) : (
                            <>
                                <Box mb={2} mt={2}>
                                    <TextField
                                        fullWidth
                                        label={translations.fa_code}
                                        variant="outlined"
                                        required
                                        value={twoFACodeInput}
                                        onChange={(e) => setTwoFACodeInput(e.target.value)}
                                    />
                                </Box>
                                {errorMessage && (
                                    <Box mb={2}>
                                        <Alert severity="error">{errorMessage}</Alert>
                                    </Box>
                                )}
                                <Box textAlign="center">
                                    <Button
                                        variant="contained"
                                        style={{ backgroundColor: 'rgba(0, 192, 239, 1)', color: 'white', padding: '8px 64px' }}
                                        onClick={handleValidate2FACode}
                                    >
                                        {translations.validate_code}
                                    </Button>
                                </Box>
                            </>
                        )}
                    </LoginForm>
                )}
            </Box>
        </LoginContainer>
    );
};

export default Login;
